import { useEffect } from "react";
import { useUserSettingsStore } from "@/features/preferences/user-settings.store";
import { useAuth } from "@/app/context/AuthContext";
import type { FocusCohort, PatternFocus, PatternStatsResponse } from "./patterns.api";
import { patternQueryKey, usePatternsStore } from "./patterns.store";

export interface UsePatternsReturn {
  stats: PatternStatsResponse | null;
  isLoading: boolean;
  error: string | null;
  setFocus: (focus: PatternFocus) => void;
}

export function usePatterns(cohort: FocusCohort, exactTimeControl?: string, enabled = true): UsePatternsReturn {
  const period = useUserSettingsStore((state) => state.settings.patternsDefaultPeriod);
  const { userId } = useAuth();
  const key = patternQueryKey(cohort, exactTimeControl);
  const ownerUserId = usePatternsStore((state) => state.ownerUserId);
  const isCurrentOwner = ownerUserId === userId;
  const stats = usePatternsStore((state) => (enabled && isCurrentOwner ? (state.statsByScope[key] ?? null) : null));
  const isLoading = usePatternsStore((state) =>
    enabled
      ? !isCurrentOwner || (!state.statsByScope[key] && (state.loadingByScope[key] ?? state.errorsByScope[key] == null))
      : false,
  );
  const error = usePatternsStore((state) =>
    enabled && isCurrentOwner && !state.statsByScope[key] ? (state.errorsByScope[key] ?? null) : null,
  );
  const setOwner = usePatternsStore((state) => state.setOwner);
  const loadPatterns = usePatternsStore((state) => state.loadPatterns);
  const updateFocus = usePatternsStore((state) => state.setFocus);

  useEffect(() => setOwner(userId), [setOwner, userId]);
  useEffect(() => {
    if (enabled) void loadPatterns(cohort, exactTimeControl).catch(() => undefined);
  }, [cohort, enabled, exactTimeControl, loadPatterns, period]);

  return {
    stats,
    isLoading,
    error,
    setFocus: (focus) => updateFocus(cohort, exactTimeControl, focus),
  };
}
